import { BasePage } from '../BasePage/BasePage'
import { Breadcrumb } from 'src/components/Breadcrumb/Breadcrumb'
import { useAppContext } from 'src/Context'
import {
  SectionTitle,
  SectionTitleSize,
  SectionTitleStyle
} from 'src/components/SectionTitle/SectionTitle'
import {
  CMSSection,
  CMSSectionBackground
} from 'src/components/CMSSection/CMSSection'

import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { API_URL, formatNumber, formatURL } from 'src/util'
import { NumberField } from 'src/components/NumberField/NumberField'
import { TextArea } from 'src/components/TextArea/TextArea'
import { Button } from 'src/wizard/Button/Button'
import { Text } from 'src/components/Text/Text'

import {
  Period,
  Template,
  TemplateCountQuestion,
  TemplatePercentQuestion,
  TemplateQuestionType
} from 'src/types'

import styles from './ContractReport.module.css'
import { routes } from 'src/routes'
import {
  contractEndMonth,
  programmeKeyOffset,
  planKeyForWorkMonth
} from 'src/programmeKeys'
import { PercentField } from 'src/components/PercentField/PercentField'
import type { ReportSubmittedState } from 'src/components/SubmissionNotice/SubmissionNotice'
import { Select } from 'src/components/Select/Select'
import { InputField } from 'src/components/InputField/InputField'

type LabelProps = {
  label: string
  required: boolean
  children: React.ReactNode
}

function Label(props: LabelProps) {
  return (
    <label className={styles.label}>
      <span className={styles.question}>
        {props.label}
        {props.required ? '*' : ''}
      </span>
      {props.children}
    </label>
  )
}

type FieldFreeTextProps = {
  question: TemplateCountQuestion
  state: QuestionResponse | undefined
  onChange: (response: QuestionResponse) => void
}

function FieldFreeText(props: FieldFreeTextProps) {
  const value = { explanation: null, value: '', ...props.state }

  return (
    <div>
      <Label label={props.question.question} required={false}>
        <InputField
          type='number'
          steps={true}
          onChange={(event) => {
            props.onChange({
              ...value,
              value:
                Number(event.target.value) > 99999999
                  ? (99999999).toString()
                  : event.target.value
            })
          }}
          value={value.value}
        />
      </Label>
    </div>
  )
}

type FieldCountProps = {
  question: TemplateCountQuestion
  state: QuestionResponse | undefined
  onChange: (response: QuestionResponse) => void
}

function FieldCount(props: FieldCountProps) {
  const value = { explanation: null, value: '', ...props.state }

  const colour =
    (Number(value.value) ?? 0) > props.question.minimum
      ? '#fc0000'
      : (Number(value.value) ?? 0) > props.question.target
      ? '#ff6c00'
      : '#4baf4f'

  return (
    <div>
      <Label label={props.question.question} required={true}>
        <NumberField
          onChange={(event) => {
            props.onChange({
              ...value,
              value: event.target.value !== '' ? event.target.value : ''
            })
          }}
          value={value.value ? Number(value.value) : ''}
          style={{ color: colour }}
          required={true}
          step={props.question.allowDecimals ? 0.1 : 1}
        />
        <span className={styles.help}>
          Enter the number of times this occurred. Your target is{' '}
          {props.question.target} occurrence
          {props.question.target === 1 ? '' : 's'} and we expect{' '}
          {props.question.minimum} occurence
          {props.question.minimum === 1 ? '' : 's'} at maximum.
          {props.question.allowDecimals && ' Decimal values are allowed.'}
        </span>
      </Label>
      {value.value !== '' &&
      props.question.explanation !== null &&
      Number(value.value) > props.question.explanation ? (
        <Label label={'Please provide an explanation'} required={true}>
          <TextArea
            onChange={(event) => {
              props.onChange({ ...value, explanation: event.target.value })
            }}
            value={value.explanation ?? ''}
            required={true}
          />
          <span className={styles.help}>
            Root cause and your plan to resolve this for the future.
          </span>
        </Label>
      ) : null}
    </div>
  )
}

type FieldYesNoProps = {
  question: TemplatePercentQuestion
  state: QuestionResponse | undefined
  reverse?: boolean
  onChange: (response: QuestionResponse) => void
}

function FieldYesNo(props: FieldYesNoProps) {
  const value = {
    explanation: null,
    value: props.reverse ? '1' : '0',
    ...props.state
  }

  // colour the selected answer red (the "mitigation"/problem answer) or green
  // (the good answer). Which answer is the problem comes from the question's
  // `mitigation` setting, defaulting from the type when unset (#46).
  const mitigation = props.question.mitigation ?? (props.reverse ? 'none' : 'no')
  const isYes = String(value.value) === String(props.reverse ? 0 : 1)
  const colour =
    mitigation === 'none'
      ? undefined
      : isYes
      ? mitigation === 'yes'
        ? '#fc0000'
        : '#4baf4f'
      : mitigation === 'no'
      ? '#fc0000'
      : '#4baf4f'

  return (
    <div>
      <Label label={props.question.question} required={true}>
        <Select
          value={value.value ?? 0}
          style={colour ? { color: colour } : undefined}
          options={[
            { value: props.reverse ? 1 : 0, label: 'No' },
            { value: props.reverse ? 0 : 1, label: 'Yes' }
          ]}
          onChange={(event) => {
            props.onChange({ ...value, value: event.target.value })
          }}
        />
      </Label>

      {!props.reverse && value.value == '0' && (
        <Label label={'Please provide an explanation'} required={true}>
          <TextArea
            onChange={(event) => {
              props.onChange({ ...value, explanation: event.target.value })
            }}
            value={value.explanation ?? ''}
            required={true}
          />
          <span className={styles.help}>
            Root cause and your plan to resolve this for the future.
          </span>
        </Label>
      )}
    </div>
  )
}

type FieldPercentProps = {
  question: TemplatePercentQuestion
  state: QuestionResponse | undefined
  onChange: (response: QuestionResponse) => void
}

function FieldPercent(props: FieldPercentProps) {
  const value = { explanation: null, value: '', ...props.state }
  const colour =
    (Number(value.value) ?? 0) < props.question.minimum
      ? '#fc0000'
      : (Number(value.value) ?? 0) < props.question.target
      ? '#ff6c00'
      : '#4baf4f'

  return (
    <div>
      <Label label={props.question.question} required={true}>
        <PercentField
          onChange={(event) => {
            props.onChange({
              ...value,
              value: event.target.value !== '' ? event.target.value : ''
            })
          }}
          value={value.value ?? ''}
          step={props.question.allowDecimals ? 0.1 : 1}
          min={0}
          required={true}
          style={{ color: colour }}
        />
        <span className={styles.help}>
          Enter the percentage as a whole value. Your target is{' '}
          {props.question.target}% and we expect {props.question.minimum}% at
          minimum.
          {props.question.allowDecimals && ' Decimal values are allowed.'}
        </span>
      </Label>
      {value.value !== '' &&
      props.question.explanation !== null &&
      Number(value.value) < props.question.explanation ? (
        <Label label={'Please provide an explanation'} required={true}>
          <TextArea
            onChange={(event) => {
              props.onChange({ ...value, explanation: event.target.value })
            }}
            value={value.explanation ?? ''}
            required={true}
          />
          <span className={styles.help}>
            Root cause and your plan to resolve this for the future.
          </span>
        </Label>
      ) : null}
    </div>
  )
}

type QuestionResponse = {
  value: string
  explanation: string | null
}

function ContractReport() {
  const params = useParams()
  const context = useAppContext()
  const navigate = useNavigate()
  const contract = context.contracts.find((c) => c._id === params.contract)

  const [template, setTemplate] = useState<Template | null>(null)
  const [nilReturn, setNilReturn] = useState<boolean>(false)

  const [progress, setProgress] = useState<number | ''>('')
  const [state, setState] = useState<QuestionResponse[]>([])
  const [progressExplanation, setProgressExplanation] = useState<string>('')
  // backlog #32 (Contract Value KPI): optional invoiced amount for the period.
  const [invoicedAmount, setInvoicedAmount] = useState<number | ''>('')
  // backlog #32: optional explanation for the invoiced figure (e.g. below target).
  const [invoicedExplanation, setInvoicedExplanation] = useState<string>('')

  const periods =
    contract?.periods.filter((p) => {
      if (
        contract.submissions.find((s) => s.period === p && s.submitted) !==
        undefined
      )
        return false

      // return (new Date()).getTime() > (new Date(`${p}-${contract.reportDueDay}`)).getTime()
      return true
    }) ?? []

  const [period, setPeriod] = useState(periods[0])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    ;(async function () {
      if (!contract) return
      const response = await fetch(`${API_URL}template/${contract.templateId}`)

      if (!response.ok) return

      const template = (await response.json()) as Template
      setTemplate(template)

      if (contract.openReport) {
        setState(contract.openReport.responses)
        // #18: prefill the #32 fields from the open report — without this,
        // re-opening a draft blanked them and submitting overwrote the
        // previously entered values with null.
        if (contract.openReport.invoicedAmount != null)
          setInvoicedAmount(contract.openReport.invoicedAmount)
        if (contract.openReport.invoicedExplanation != null)
          setInvoicedExplanation(contract.openReport.invoicedExplanation)
        if (contract.openReport.progressExplanation != null)
          setProgressExplanation(contract.openReport.progressExplanation)
      } else setState(new Array(template.questions.length))
    })()
  }, [contract])

  // let period : string | null = contract?.periods.find(p => {
  //   if (contract.submissions.find(s => s.period === p && s.submitted) !== undefined)
  //     return false

  //   return true
  // }) ?? null

  // if (contract === undefined || template === null || period === null)
  //   return null

  if (contract === undefined || template === null) return null

  // #32 REGRESSION: this page looks the plan up by the report period, i.e. by
  // the work month. On a programme saved before b069cb1 the allocation key is
  // the report-due month, one ahead of the work it plans, so the exact-key
  // lookup returned the PREVIOUS month's plan — the contractor submitting July
  // was shown June's figure and judged on-target against June's target. Derive
  // the contract's key offset from the stored data (the same derivation the
  // server guard and the programme page use — see src/programmeKeys.ts) and
  // route every allocation lookup through it. offset 0 leaves behaviour
  // exactly as it was for contracts whose keys really are work-month-keyed.
  const programmeOffset = programmeKeyOffset(
    Object.keys(contract.programme?.allocation ?? {}),
    contract.periods[0],
    // #53: the tail discriminator — the contract's last work month, read with
    // the same +2h nudge as the server, so this page derives the same offset
    // the server guard does
    contractEndMonth(contract.endDate)
  )

  // the allocation key holding the plan for a given work month
  const planKey = (workMonth: string) =>
    planKeyForWorkMonth(workMonth, programmeOffset)

  const onTrack = (function () {
    if (contract.useProgramme === false) return null

    if (contract.programme?.allocation[planKey(period)] == null) return null

    if (progress === '') return null

    return progress >= contract.programme.allocation[planKey(period)]
  })()

  // year-to-date planned vs actual for the selected period
  // (periods are YYYY-MM strings so lexical comparison preserves order)
  const ytd = (function () {
    if (contract.useProgramme === false || contract.programme == null)
      return null
    if (period == null) return null
    if (progress === '') return null

    const allocation = contract.programme.allocation

    // #32 REGRESSION: compare keys against the key for the selected work
    // month, not against the work month itself. On an offset programme every
    // key sorts one month ahead, so `p <= period` stopped a month early and
    // understated YTD Planned by exactly one month's allocation.
    const upTo = planKey(period)

    let planned = 0
    for (const p of Object.keys(allocation)) {
      if (p <= upTo) planned += allocation[p] ?? 0
    }

    // actual completed for prior submitted periods plus the current input
    let actual = Number(progress)
    for (const s of contract.submissions) {
      if (s.period < period && s.completed != null && !isNaN(s.completed))
        actual += s.completed
    }

    return { planned, actual }
  })()

  // require an explanation when YTD actual falls below 80% of YTD planned
  const ytdBelowThreshold =
    ytd !== null && ytd.planned > 0 && ytd.actual < 0.8 * ytd.planned

  const progressExplanationRequired = !nilReturn && ytdBelowThreshold

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)

    if (progressExplanationRequired && progressExplanation.trim() === '') {
      setError(
        'Your year-to-date progress is below 80% of plan. Please provide an explanation before submitting.'
      )
      return
    }

    setSubmitting(true)

    const isFinalSubmit = true

    if (contract === undefined || template === null) return

    let valid = null
    let succeeded = false

    try {
      const data = new URLSearchParams()
      data.append('username', context.user?.username as string)
      data.append('contractId', contract._id)
      data.append('progress', progress.toString())

      // #40: stamp each answer with the text of the question it was displayed
      // against, so the server can pair answers by identity rather than by
      // array position — a template edited while this form was open would
      // otherwise attach answers to the wrong questions at save time.
      let submissionState: (Partial<QuestionResponse> & {
        question: string
      })[] = template.questions.map((q, i) => ({
        ...state[i],
        question: q.question
      }))
      if (nilReturn) {
        submissionState = []
      }
      data.append('state', JSON.stringify(submissionState))
      data.append('nilReturn', JSON.stringify(nilReturn))

      data.append('period', period as string)
      data.append('submission', contract.openReport?._id ?? '')
      data.append('submit', JSON.stringify(isFinalSubmit))
      data.append(
        'progressExplanation',
        nilReturn ? '' : progressExplanation
      )
      // backlog #32: optional, only sent when the contractor entered a value
      data.append(
        'invoicedAmount',
        nilReturn || invoicedAmount === '' ? '' : invoicedAmount.toString()
      )
      data.append(
        'invoicedExplanation',
        nilReturn || invoicedAmount === '' ? '' : invoicedExplanation
      )

      const response = await fetch(`${API_URL}report`, {
        method: 'post',
        headers: {
          Authorization: `Token ${context.user?.token}`
        },
        body: data
      })

      if (response.ok === false) {
        // #40: the server puts the explanation (e.g. "the reporting template
        // has changed, please reload") in the response body — statusText is
        // just "Bad Request"/"Conflict" and hides it.
        let message = ''
        try {
          const body = await response.json()
          if (typeof body?.message === 'string') message = body.message
        } catch {
          // no JSON body — fall through to the generic message
        }
        setError(message || 'The report could not be saved. Please try again.')
        return
      }

      const json = await response.json()

      if (json.success === false) {
        setError(json.message)
        return
      }

      // update local state -- modify by reference
      const contracts = [...context.contracts]
      const index = contracts.findIndex((c) => c._id === contract._id)

      const updated = { ...json.contract }
      valid = updated.programmeValid
      if (index >= 0) {
        contracts[index] = {
          ...updated,
          siteStartDate: new Date(updated.siteStartDate as string),
          endDate: new Date(updated.endDate as string),
          reportStartDate: new Date(updated.reportStartDate as string),
          dueFirst: new Date(updated.dueFirst as string),
          dueNext: new Date(updated.dueNext as string),
          createdAt: new Date(updated.createdAt as string)
        }
      } else {
        // this should never happen, but if it does just add it on the end
        contracts.push({
          ...updated,
          siteStartDate: new Date(updated.siteStartDate as string),
          endDate: new Date(updated.endDate as string),
          reportStartDate: new Date(updated.reportStartDate as string),
          dueFirst: new Date(updated.dueFirst as string),
          dueNext: new Date(updated.dueNext as string),
          createdAt: new Date(updated.createdAt as string)
        })
      }
      context.setContext({ ...context, contracts })
      succeeded = true
    } catch {
      setError(
        'A network error occurred. Please check your internet connection and try again.'
      )
      return
    } finally {
      setSubmitting(false)
      // #40: only leave the page when the save actually went through —
      // navigating away on an error used to hide the message just set.
      if (succeeded) {
        // #54: carry a confirmation through the redirect — without it a
        // successful submission landed the contractor on another page with no
        // explanation and read as an error.
        const state: ReportSubmittedState = {
          reportSubmitted: { contract: contract.name, period }
        }
        if (valid === null || valid === true) {
          navigate(routes.member.path, { state })
        } else {
          if (routes.member.children) {
            const url = formatURL(routes.member.children.programme.path, {
              contract: contract._id
            })
            navigate(url, {
              state: { ...state, programmeNeedsAttention: true }
            })
          }
        }
      }
    }
  }

  //period = period ? period : '' as Period

  if (periods.length === 0) {
    return (
      <BasePage>
        <Breadcrumb />

        <CMSSection
          primary={
            <>
              <SectionTitle
                title={contract.name}
                size={SectionTitleSize.Small}
                style={SectionTitleStyle.Dark}
              />
              <Text>All reports are sent.</Text>
            </>
          }
          background={CMSSectionBackground.White}
        />
      </BasePage>
    )
  }

  return (
    <BasePage>
      <Breadcrumb />

      <CMSSection
        primary={
          <>
            <SectionTitle
              title={contract.name}
              size={SectionTitleSize.Small}
              style={SectionTitleStyle.Dark}
            />

            <form onSubmit={handleSubmit}>
              <div>
                <Label label={'Reporting Period'} required={true}>
                  <Select
                    value={period}
                    options={periods.map((p) => ({ label: p, value: p }))}
                    onChange={(e) => setPeriod(e.target.value as Period)}
                  />
                </Label>
              </div>

              {/* Nil Return Checkbox */}
              {template?.enableNilReturn && (
                <div
                  style={{
                    margin: '20px 0',
                    padding: '10px',
                    border: '1px solid #eee',
                    borderRadius: '4px'
                  }}
                >
                  <label
                    htmlFor='nilReturnCheckbox'
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      id='nilReturnCheckbox'
                      type='checkbox'
                      checked={nilReturn}
                      onChange={(e) => {
                        setNilReturn(e.target.checked)
                        if (e.target.checked) {
                          setState([]) // Clear responses when nil return is checked
                        }
                      }}
                      style={{
                        marginRight: '10px',
                        height: '1.2em',
                        width: '1.2em'
                      }}
                    />
                    <span style={{ fontWeight: 'bold' }}>
                      Nil Return (No work completed this period)
                    </span>
                  </label>
                  {nilReturn && (
                    <div
                      style={{
                        fontSize: '0.9em',
                        color: '#555',
                        marginTop: '5px',
                        paddingLeft: '28px'
                      }}
                    >
                      You are submitting a Nil Return. No KPI data is required.
                    </div>
                  )}
                </div>
              )}

              {/* Conditional rendering for KPI questions */}
              {!nilReturn && (
                <>
                  {contract.useProgramme ? (
                    <div>
                      <Label label={'Jobs completed'} required={true}>
                        <NumberField
                          onChange={(event) => {
                            setProgress(
                              event.target.value !== ''
                                ? parseInt(event.target.value)
                                : ''
                            )
                          }}
                          value={progress}
                          step={1}
                          min={0}
                          required={true}
                          style={{
                            color:
                              onTrack === true
                                ? '#4baf4f'
                                : onTrack === false
                                ? '#fc0000'
                                : 'black'
                          }}
                        />
                        <span className={styles.help}>
                          Enter the number of jobs completed this reporting
                          period as a whole number. Your programme has{' '}
                          {formatNumber(
                            contract.programme?.allocation[planKey(period)]
                          )}{' '}
                          jobs scheduled for completion for this period.
                        </span>
                      </Label>

                      {progressExplanationRequired ? (
                        <Label
                          label={'Please provide an explanation'}
                          required={true}
                        >
                          <TextArea
                            onChange={(event) => {
                              setProgressExplanation(event.target.value)
                            }}
                            value={progressExplanation}
                            required={true}
                          />
                          <span className={styles.help}>
                            Your year-to-date progress (
                            {formatNumber(ytd?.actual)} of{' '}
                            {formatNumber(ytd?.planned)} scheduled jobs) is below
                            80% of plan. Please provide the root cause and your
                            plan to resolve this for the future.
                          </span>
                        </Label>
                      ) : null}
                    </div>
                  ) : null}

                  {template.questions.map((q, i) => {
                    const onChange = (response: QuestionResponse) => {
                      setState([
                        ...state.slice(0, i),
                        response,
                        ...state.slice(i + 1)
                      ])
                    }
                    switch (q.type) {
                      case TemplateQuestionType.Count:
                        return (
                          <FieldCount
                            key={i}
                            question={q}
                            state={state[i]}
                            onChange={onChange}
                          />
                        )
                      case TemplateQuestionType.Percent:
                        return (
                          <FieldPercent
                            key={i}
                            question={q}
                            state={state[i]}
                            onChange={onChange}
                          />
                        )
                      case TemplateQuestionType.YesNo:
                        return (
                          <FieldYesNo
                            key={i}
                            question={q}
                            state={state[i]}
                            onChange={onChange}
                          />
                        )
                      case TemplateQuestionType.YesNoReverse:
                        return (
                          <FieldYesNo
                            key={i}
                            reverse={true}
                            question={q}
                            state={state[i]}
                            onChange={onChange}
                          />
                        )
                      case TemplateQuestionType.FreeText:
                        return (
                          <FieldFreeText
                            key={i}
                            question={q}
                            state={state[i]}
                            onChange={onChange}
                          />
                        )
                    }
                  })}

                  {/* backlog #32 (Contract Value KPI) — optional invoiced
                      amount for this reporting period. Additive: never
                      required, never blocks submission. */}
                  <Label label={'Amount invoiced this period (£)'} required={false}>
                    <NumberField
                      onChange={(event) => {
                        setInvoicedAmount(
                          event.target.value !== ''
                            ? Number(event.target.value)
                            : ''
                        )
                      }}
                      value={invoicedAmount}
                      step={'any'}
                      min={0}
                    />
                    <span className={styles.help}>
                      Enter the total amount invoiced for this contract during
                      this reporting period. Leave blank if not applicable.
                    </span>
                  </Label>

                  {/* backlog #32 — when an invoiced amount is entered, offer an
                      explanation (e.g. if it is below the contract-value target).
                      Captured on the submission and embedded in the
                      "Contract Value Below 85%" helpdesk ticket. */}
                  {invoicedAmount !== '' ? (
                    <Label
                      label={'Explanation for the invoiced value (if below target)'}
                      required={false}
                    >
                      <TextArea
                        onChange={(event) =>
                          setInvoicedExplanation(event.target.value)
                        }
                        value={invoicedExplanation}
                      />
                      <span className={styles.help}>
                        If your invoiced value is below the contract-value target,
                        please explain why. This is passed to the EP team.
                      </span>
                    </Label>
                  ) : null}
                </>
              )}

              {onTrack === false ? (
                <Text>
                  If you are falling behind your planned schedule and are likely
                  to need an extension or to change your contract, please{' '}
                  <a href={routes.contact.path} className={styles.link}>
                    contact us
                  </a>{' '}
                  as soon as possible.
                </Text>
              ) : null}

              {error ? <Text className={styles.error}>{error}</Text> : null}
              <div className={styles.buttons}>
                <Button
                  name={'submit'}
                  submit={true}
                  colour={'green'}
                  label={'Submit'}
                  disabled={
                    submitting || contract === undefined || template === null
                  }
                />
              </div>
            </form>
          </>
        }
        background={CMSSectionBackground.White}
      />
    </BasePage>
  )
}

export { ContractReport }
